import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { recordUploadHubActivity } from './activityLedgerService'
import { validateAction, validateTrigger } from './automationRegistry'
import { validateAutomationGraphShape } from './automationGraphRules'
import { canSendWithChatControl, type ChatControl } from '../types/memberControls'
import type {
  AutomationAction,
  AutomationActionType,
  AutomationCondition,
  AutomationConstraint,
  AutomationDefinition,
  AutomationGraph,
  AutomationKind,
  AutomationRun,
  AutomationRunOutcome,
  AutomationStatus,
  AutomationTrigger,
  AutomationTriggerType,
} from '../types/automation'

const AUTOMATIONS_COLLECTION = 'automation_definitions'

export type AutomationActor = { id: string; name?: string }

type CreateAutomationInput = {
  workspaceId: string
  name: string
  description?: string
  trigger: AutomationTrigger
  actions: AutomationAction[]
  actor: AutomationActor
}

type UpdateAutomationInput = {
  workspaceId: string
  id: string
  patch: Partial<{
    name: string
    description?: string
    trigger: AutomationTrigger
    actions: AutomationAction[]
  }>
  actor: AutomationActor
}

const LIVE_CUTOVER_TRIGGER_TYPES = new Set<AutomationTriggerType>([
  'NODE_CREATED',
  'CHECKLIST_CREATED',
  'CHECKLIST_COMPLETED',
  'PROOF_UPDATED',
  'TASK_ASSIGNED',
  'DATE_REACHED',
  'FORM_SUBMITTED',
  'UPLOAD_COMPLETED',
  'NODE_COMPLETED',
  'AI_ESSAY_GENERATED',
  'AI_REFINE_USED',
  'FOCUS_BLOCK_COMPLETED',
  'SPIN_WON',
  'CHAT_MESSAGE_POSTED',
])

const LIVE_CUTOVER_ACTION_TYPES = new Set<AutomationActionType>([
  'CREATE_NODE',
  'CREATE_CHECKLIST_ITEM',
  'NOTIFY_USER',
  'UPDATE_FIELD',
  'WEBHOOK',
  'COMPLETE_NODE',
  'ASSIGN_TASK',
  'SEND_CHAT_MESSAGE',
  'LOG_TO_LEDGER',
])

export const getLegacyAutomationLiveMigrationBlockReason = (automation: AutomationDefinition): string | null => {
  if (automation.status !== 'ACTIVE') return null
  if (!LIVE_CUTOVER_TRIGGER_TYPES.has(automation.trigger.type)) {
    return 'This active workflow still depends on a legacy-only trigger path. Pause it before moving it into the unified builder.'
  }
  if (automation.actions.some((action) => !LIVE_CUTOVER_ACTION_TYPES.has(action.type))) {
    return 'This active workflow still depends on a legacy-only action path. Pause it before moving it into the unified builder.'
  }
  return null
}

export const canMigrateLegacyAutomationLive = (automation: AutomationDefinition): boolean =>
  getLegacyAutomationLiveMigrationBlockReason(automation) === null

export const normalizeAutomation = (snapshot: { id: string; data: () => DocumentData }): AutomationDefinition => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    name: typeof data.name === 'string' ? data.name : 'Untitled automation',
    description: typeof data.description === 'string' ? data.description : '',
    trigger: data.trigger as AutomationTrigger,
    actions: Array.isArray(data.actions) ? (data.actions as AutomationAction[]) : [],
    status: (data.status as AutomationStatus) ?? 'PAUSED',
    disabledReason: typeof data.disabledReason === 'string' ? data.disabledReason : undefined,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    lastRun: data.lastRun ?? null,
    lastRunOutcome: data.lastRunOutcome as AutomationRunOutcome | undefined,
    kind: (typeof data.kind === 'string' ? data.kind : 'GENERIC') as AutomationKind,
    // Fallback to legacy pre-rename field name `linkedTemplateId` that existing Firestore docs may still carry
    linkedBookId: typeof data.linkedBookId === 'string'
      ? data.linkedBookId
      : typeof data.linkedTemplateId === 'string'
        ? data.linkedTemplateId
        : undefined,
  }
}

export const subscribeToAutomations = (
  workspaceId: string,
  callback: (automations: AutomationDefinition[]) => void,
  onError?: (error: Error) => void,
) => {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const q = query(
    collection(firestoreDb, AUTOMATIONS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map(normalizeAutomation)),
    (error) => onError?.(error),
  )
}

const writeAuditEntry = async (
  workspaceId: string,
  actor: AutomationActor,
  action: string,
  automation: { id: string; name: string },
  summary: string,
  metadata: Record<string, unknown> = {},
) => {
  if (!workspaceId || !actor.id) return
  await recordUploadHubActivity({
    workspaceId,
    userId: actor.id,
    userName: actor.name,
    action,
    targetId: automation.id,
    targetTitle: automation.name,
    targetType: 'AUTOMATION',
    summary,
    metadata,
  })
}

export const assertCreatorCanPostChat = async (
  workspaceId: string,
  actorId: string,
  actions: AutomationAction[],
): Promise<void> => {
  const hasChatAction = actions.some((a) => a.type === 'SEND_CHAT_MESSAGE')
  if (!hasChatAction || !workspaceId || !actorId) return

  // Check if actor is an owner of the workspace; owners are exempt from chat controls
  try {
    const workspaceSnap = await getDoc(doc(firestoreDb, `workspaces/${workspaceId}`))
    if (workspaceSnap.exists()) {
      const wsData = workspaceSnap.data()
      if (wsData?.ownerId === actorId || (Array.isArray(wsData?.ownerIds) && wsData.ownerIds.includes(actorId))) {
        return
      }
    }
  } catch {
    // If workspace read fails or offline, fall through to mirror check
  }

  // Check membership mirror doc
  try {
    const mirrorSnap = await getDoc(doc(firestoreDb, `users/${actorId}/workspaceMemberships/${workspaceId}`))
    if (!mirrorSnap.exists()) return
    const mirrorData = mirrorSnap.data()
    const chatControl = (mirrorData?.effectiveControls?.chat as ChatControl) ?? 'FULL'
    if (chatControl === 'OFF') {
      throw new Error('Cannot save automation with chat message actions: chat is disabled for your account in this workspace.')
    }

    const chatActions = actions.filter((a): a is Extract<AutomationAction, { type: 'SEND_CHAT_MESSAGE' }> => a.type === 'SEND_CHAT_MESSAGE')
    for (const action of chatActions) {
      const channelId = action.config?.channelId
      let channelData: Record<string, unknown> | null = null
      if (channelId) {
        try {
          const channelSnap = await getDoc(doc(firestoreDb, `workspaces/${workspaceId}/channels/${channelId}`))
          if (channelSnap.exists()) {
            channelData = channelSnap.data() as Record<string, unknown>
          }
        } catch {
          // If channel read fails, treat as null
        }
      }
      if (!canSendWithChatControl(chatControl, channelData)) {
        throw new Error(`Cannot save automation with chat message actions: chat is disabled for channel "${channelId ?? ''}" under ${chatControl} control.`)
      }
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('chat is disabled')) throw error
  }
}

const validateDefinition = (trigger: AutomationTrigger, actions: AutomationAction[]) => {
  const triggerError = validateTrigger(trigger)
  if (triggerError) throw new Error(triggerError)
  if (actions.length === 0) throw new Error('Add at least one action.')
  for (const action of actions) {
    const error = validateAction(action)
    if (error) throw new Error(error)
  }
}

export const createAutomation = async (input: CreateAutomationInput): Promise<string> => {
  validateDefinition(input.trigger, input.actions)
  await assertCreatorCanPostChat(input.workspaceId, input.actor.id, input.actions)
  const ref = await addDoc(collection(firestoreDb, AUTOMATIONS_COLLECTION), {
    workspaceId: input.workspaceId,
    name: input.name.trim() || 'Untitled automation',
    description: input.description?.trim() ?? '',
    trigger: input.trigger,
    actions: input.actions,
    status: 'PAUSED' as AutomationStatus,
    createdBy: input.actor.id,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastRun: null,
    lastRunOutcome: null,
  })
  await writeAuditEntry(
    input.workspaceId,
    input.actor,
    'AUTOMATION_CREATED',
    { id: ref.id, name: input.name },
    `Created automation "${input.name}"`,
    { triggerType: input.trigger.type, actionCount: input.actions.length },
  )
  return ref.id
}

export const updateAutomation = async (input: UpdateAutomationInput): Promise<void> => {
  if (input.patch.trigger && input.patch.actions) {
    validateDefinition(input.patch.trigger, input.patch.actions)
  }
  if (input.patch.actions) {
    await assertCreatorCanPostChat(input.workspaceId, input.actor.id, input.patch.actions)
  }
  const ref = doc(firestoreDb, AUTOMATIONS_COLLECTION, input.id)
  await updateDoc(ref, {
    ...(input.patch.name !== undefined ? { name: input.patch.name.trim() || 'Untitled automation' } : {}),
    ...(input.patch.description !== undefined ? { description: input.patch.description?.trim() ?? '' } : {}),
    ...(input.patch.trigger !== undefined ? { trigger: input.patch.trigger } : {}),
    ...(input.patch.actions !== undefined ? { actions: input.patch.actions } : {}),
    updatedAt: serverTimestamp(),
  })
  await writeAuditEntry(
    input.workspaceId,
    input.actor,
    'AUTOMATION_UPDATED',
    { id: input.id, name: input.patch.name ?? 'automation' },
    `Updated automation "${input.patch.name ?? input.id}"`,
  )
}

export const setAutomationStatus = async (
  workspaceId: string,
  actor: AutomationActor,
  id: string,
  name: string,
  status: AutomationStatus,
  disabledReason?: string,
): Promise<void> => {
  const ref = doc(firestoreDb, AUTOMATIONS_COLLECTION, id)
  await updateDoc(ref, {
    status,
    disabledReason: status === 'DISABLED' ? disabledReason ?? 'Disabled by automation engine.' : null,
    updatedAt: serverTimestamp(),
  })
  await writeAuditEntry(
    workspaceId,
    actor,
    status === 'DISABLED' ? 'AUTOMATION_DISABLED' : 'AUTOMATION_TOGGLED',
    { id, name },
    `Automation "${name}" set to ${status}`,
    { status, disabledReason: disabledReason ?? null },
  )
}

export const deleteAutomation = async (
  workspaceId: string,
  actor: AutomationActor,
  id: string,
  name: string,
): Promise<void> => {
  await deleteDoc(doc(firestoreDb, AUTOMATIONS_COLLECTION, id))
  await writeAuditEntry(
    workspaceId,
    actor,
    'AUTOMATION_DELETED',
    { id, name },
    `Deleted automation "${name}"`,
  )
}

export type RetryAutomationRunInput = {
  runId: string
  workflowCollection: 'automation_definitions' | 'automation_graphs'
  workflowId?: string
}

export type RetryAutomationRunResult = {
  runId: string
  sourceRunId: string
  attemptCount: number
}

const retryAutomationRunCallable = httpsCallable<RetryAutomationRunInput, RetryAutomationRunResult>(
  firebaseFunctions,
  'retryAutomationRun',
)

export const retryAutomationRun = async (
  input: RetryAutomationRunInput,
): Promise<RetryAutomationRunResult> => {
  const response = await retryAutomationRunCallable(input)
  return response.data
}

export const recordAutomationRun = async (
  workspaceId: string,
  actor: AutomationActor,
  automationId: string,
  automationName: string,
  run: Omit<AutomationRun, 'id' | 'automationId' | 'workspaceId'>,
  options: { skipAudit?: boolean } = {},
): Promise<string> => {
  const subcollection = collection(
    firestoreDb,
    AUTOMATIONS_COLLECTION,
    automationId,
    'automation_runs',
  )
  const ref = doc(subcollection)
  await setDoc(ref, {
    ...run,
    automationId,
    workspaceId,
  })
  await updateDoc(doc(firestoreDb, AUTOMATIONS_COLLECTION, automationId), {
    lastRun: serverTimestamp(),
    lastRunOutcome: run.outcome,
  })
  if (!options.skipAudit) await writeAuditEntry(
    workspaceId,
    actor,
    run.outcome === 'SUCCESS'
      ? 'AUTOMATION_EXECUTED'
      : run.outcome === 'SIMULATED'
        ? 'AUTOMATION_SIMULATED'
        : run.outcome === 'SKIPPED'
          ? 'AUTOMATION_TRIGGERED'
          : 'AUTOMATION_FAILED',
    { id: automationId, name: automationName },
    run.outcome === 'SUCCESS'
      ? `Automation "${automationName}" executed`
      : run.outcome === 'SIMULATED'
        ? `Automation "${automationName}" simulated`
        : run.outcome === 'SKIPPED'
          ? `Automation "${automationName}" triggered but skipped`
          : `Automation "${automationName}" failed: ${run.errorMessage ?? 'unknown error'}`,
    { runId: ref.id, outcome: run.outcome },
  )
  return ref.id
}

// ---------------------------------------------------------------------------
// v2 — AutomationGraph CRUD
// Mirrors the v1 shape above so callers can pick between definitions and
// graphs without a second mental model. Graphs live in `automation_graphs`.
// ---------------------------------------------------------------------------

const GRAPHS_COLLECTION = 'automation_graphs'

type CreateGraphInput = {
  id?: string
  workspaceId: string
  name: string
  description?: string
  triggers: AutomationTrigger[]
  constraint?: AutomationConstraint
  if?: AutomationCondition
  then: AutomationAction[]
  else?: AutomationAction[]
  actor: AutomationActor
}

type UpdateGraphInput = {
  workspaceId: string
  id: string
  patch: Partial<{
    name: string
    description?: string
    triggers: AutomationTrigger[]
    constraint?: AutomationConstraint | null
    if?: AutomationCondition | null
    then: AutomationAction[]
    else?: AutomationAction[] | null
  }>
  actor: AutomationActor
}

const validateGraph = (
  triggers: AutomationTrigger[],
  thenActions: AutomationAction[],
  ifCondition?: AutomationCondition | null,
  elseActions?: AutomationAction[],
) => {
  const shapeError = validateAutomationGraphShape({
    triggers,
    if: ifCondition,
    then: thenActions,
    else: elseActions,
  })
  if (shapeError) throw new Error(shapeError)
  for (const trigger of triggers) {
    const err = validateTrigger(trigger)
    if (err) throw new Error(err)
  }
  for (const action of thenActions) {
    const err = validateAction(action)
    if (err) throw new Error(err)
  }
  for (const action of elseActions ?? []) {
    const err = validateAction(action)
    if (err) throw new Error(err)
  }
}

const normalizeGraph = (snapshot: QueryDocumentSnapshot<DocumentData>): AutomationGraph => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    name: typeof data.name === 'string' ? data.name : 'Untitled automation',
    description: typeof data.description === 'string' ? data.description : '',
    version: 2,
    triggers: Array.isArray(data.triggers) ? (data.triggers as AutomationTrigger[]) : [],
    constraint: (data.constraint as AutomationConstraint | undefined) ?? undefined,
    if: (data.if as AutomationCondition | undefined) ?? undefined,
    then: Array.isArray(data.then) ? (data.then as AutomationAction[]) : [],
    else: Array.isArray(data.else) ? (data.else as AutomationAction[]) : undefined,
    status: (data.status as AutomationStatus) ?? 'PAUSED',
    disabledReason: typeof data.disabledReason === 'string' ? data.disabledReason : undefined,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    lastRun: data.lastRun ?? null,
    lastRunOutcome: data.lastRunOutcome as AutomationRunOutcome | undefined,
  }
}

export const subscribeToAutomationGraphs = (
  workspaceId: string,
  callback: (graphs: AutomationGraph[]) => void,
  onError?: (error: Error) => void,
) => {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const q = query(
    collection(firestoreDb, GRAPHS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map(normalizeGraph)),
    (error) => onError?.(error),
  )
}

export const createAutomationGraph = async (input: CreateGraphInput): Promise<string> => {
  validateGraph(input.triggers, input.then, input.if, input.else)
  const allActions = [...input.then, ...(input.else ?? [])]
  await assertCreatorCanPostChat(input.workspaceId, input.actor.id, allActions)
  const payload = {
    workspaceId: input.workspaceId,
    name: input.name.trim() || 'Untitled automation',
    description: input.description?.trim() ?? '',
    version: 2,
    triggers: input.triggers,
    constraint: input.constraint ?? null,
    if: input.if ?? null,
    then: input.then,
    else: input.else ?? null,
    status: 'PAUSED' as AutomationStatus,
    createdBy: input.actor.id,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastRun: null,
    lastRunOutcome: null,
  }
  const ref = input.id
    ? doc(firestoreDb, GRAPHS_COLLECTION, input.id)
    : doc(collection(firestoreDb, GRAPHS_COLLECTION))
  await setDoc(ref, payload)
  await writeAuditEntry(
    input.workspaceId,
    input.actor,
    'AUTOMATION_CREATED',
    { id: ref.id, name: input.name },
    `Created automation graph "${input.name}"`,
    {
      version: 2,
      triggerCount: input.triggers.length,
      thenCount: input.then.length,
      elseCount: input.else?.length ?? 0,
      hasIf: Boolean(input.if),
      hasConstraint: Boolean(input.constraint),
    },
  )
  return ref.id
}

export const updateAutomationGraph = async (input: UpdateGraphInput): Promise<void> => {
  if (input.patch.triggers && input.patch.then) {
    validateGraph(input.patch.triggers, input.patch.then, input.patch.if ?? undefined, input.patch.else ?? undefined)
  }
  if (input.patch.then || input.patch.else) {
    const allActions = [...(input.patch.then ?? []), ...(input.patch.else ?? [])]
    await assertCreatorCanPostChat(input.workspaceId, input.actor.id, allActions)
  }
  const ref = doc(firestoreDb, GRAPHS_COLLECTION, input.id)
  await updateDoc(ref, {
    ...(input.patch.name !== undefined ? { name: input.patch.name.trim() || 'Untitled automation' } : {}),
    ...(input.patch.description !== undefined ? { description: input.patch.description?.trim() ?? '' } : {}),
    ...(input.patch.triggers !== undefined ? { triggers: input.patch.triggers } : {}),
    ...(input.patch.constraint !== undefined ? { constraint: input.patch.constraint ?? null } : {}),
    ...(input.patch.if !== undefined ? { if: input.patch.if ?? null } : {}),
    ...(input.patch.then !== undefined ? { then: input.patch.then } : {}),
    ...(input.patch.else !== undefined ? { else: input.patch.else ?? null } : {}),
    updatedAt: serverTimestamp(),
  })
  await writeAuditEntry(
    input.workspaceId,
    input.actor,
    'AUTOMATION_UPDATED',
    { id: input.id, name: input.patch.name ?? 'automation' },
    `Updated automation graph "${input.patch.name ?? input.id}"`,
  )
}

export const setAutomationGraphStatus = async (
  workspaceId: string,
  actor: AutomationActor,
  id: string,
  name: string,
  status: AutomationStatus,
  disabledReason?: string,
): Promise<void> => {
  const ref = doc(firestoreDb, GRAPHS_COLLECTION, id)
  await updateDoc(ref, {
    status,
    disabledReason: status === 'DISABLED' ? disabledReason ?? 'Disabled by automation engine.' : null,
    updatedAt: serverTimestamp(),
  })
  await writeAuditEntry(
    workspaceId,
    actor,
    status === 'DISABLED' ? 'AUTOMATION_DISABLED' : 'AUTOMATION_TOGGLED',
    { id, name },
    `Automation graph "${name}" set to ${status}`,
    { status, disabledReason: disabledReason ?? null },
  )
}

export const deleteAutomationGraph = async (
  workspaceId: string,
  actor: AutomationActor,
  id: string,
  name: string,
): Promise<void> => {
  await deleteDoc(doc(firestoreDb, GRAPHS_COLLECTION, id))
  await writeAuditEntry(
    workspaceId,
    actor,
    'AUTOMATION_DELETED',
    { id, name },
    `Deleted automation graph "${name}"`,
  )
}

export const recordAutomationGraphRun = async (
  workspaceId: string,
  actor: AutomationActor,
  graphId: string,
  graphName: string,
  run: Omit<AutomationRun, 'id' | 'automationId' | 'workspaceId'>,
  branch: 'THEN' | 'ELSE' | 'NONE',
  options: { skipAudit?: boolean } = {},
): Promise<string> => {
  const subcollection = collection(firestoreDb, GRAPHS_COLLECTION, graphId, 'automation_runs')
  const ref = doc(subcollection)
  await setDoc(ref, { ...run, automationId: graphId, workspaceId, branch })
  await updateDoc(doc(firestoreDb, GRAPHS_COLLECTION, graphId), {
    lastRun: serverTimestamp(),
    lastRunOutcome: run.outcome,
  })
  if (!options.skipAudit) await writeAuditEntry(
    workspaceId,
    actor,
    run.outcome === 'SUCCESS'
      ? 'AUTOMATION_EXECUTED'
      : run.outcome === 'SIMULATED'
        ? 'AUTOMATION_SIMULATED'
        : run.outcome === 'SKIPPED'
          ? 'AUTOMATION_TRIGGERED'
          : 'AUTOMATION_FAILED',
    { id: graphId, name: graphName },
    `Graph "${graphName}" ${run.outcome.toLowerCase()} on branch ${branch}`,
    { runId: ref.id, outcome: run.outcome, branch },
  )
  return ref.id
}

type MigrateAutomationToGraphInput = {
  workspaceId: string
  sourceAutomation: AutomationDefinition
  name: string
  description?: string
  triggers: AutomationTrigger[]
  constraint?: AutomationConstraint
  if?: AutomationCondition
  then: AutomationAction[]
  else?: AutomationAction[]
  actor: AutomationActor
}

export const migrateAutomationToGraph = async (input: MigrateAutomationToGraphInput): Promise<string> => {
  const { sourceAutomation, workspaceId, actor } = input
  const automation = sourceAutomation
  if (automation.kind === 'RECURRING_BOOK_ROLLER') {
    throw new Error('Managed recurring workflows must stay controlled from Checklists.')
  }
  const liveMigrationBlockReason = getLegacyAutomationLiveMigrationBlockReason(automation)
  if (liveMigrationBlockReason) {
    throw new Error(liveMigrationBlockReason)
  }
  validateGraph(input.triggers, input.then, input.if, input.else)

  const graphId = automation.id
  const graphRef = doc(firestoreDb, GRAPHS_COLLECTION, graphId)
  const legacyRef = doc(firestoreDb, AUTOMATIONS_COLLECTION, automation.id)
  await runTransaction(firestoreDb, async (transaction) => {
    const [existingGraph, existingLegacy] = await Promise.all([
      transaction.get(graphRef),
      transaction.get(legacyRef),
    ])
    if (existingGraph.exists()) {
      throw new Error('An upgraded workflow already exists for this record.')
    }
    if (!existingLegacy.exists()) {
      throw new Error('This workflow no longer exists in the legacy engine.')
    }
    transaction.set(graphRef, {
      workspaceId,
      name: input.name.trim() || 'Untitled automation',
      description: input.description?.trim() ?? '',
      version: 2,
      triggers: input.triggers,
      constraint: input.constraint ?? null,
      if: input.if ?? null,
      then: input.then,
      else: input.else ?? null,
      status: automation.status,
      disabledReason: automation.status === 'DISABLED' ? automation.disabledReason ?? 'Disabled by automation engine.' : null,
      createdBy: automation.createdBy,
      createdAt: automation.createdAt ?? null,
      updatedAt: serverTimestamp(),
      lastRun: automation.lastRun ?? null,
      lastRunOutcome: automation.lastRunOutcome ?? null,
      migratedFromLegacyDefinition: true,
      migratedAt: serverTimestamp(),
    })
    transaction.delete(legacyRef)
  })

  await writeAuditEntry(
    workspaceId,
    actor,
    'AUTOMATION_UPDATED',
    { id: graphId, name: automation.name },
    `Migrated workflow "${input.name.trim() || automation.name}" into the unified builder`,
    {
      sourceCollection: AUTOMATIONS_COLLECTION,
      targetCollection: GRAPHS_COLLECTION,
      sourceAutomationId: automation.id,
      targetGraphId: graphId,
      preservedStatus: automation.status,
      lastRunOutcome: automation.lastRunOutcome ?? null,
    },
  )

  return graphId
}

const normalizeRun = (
  snapshot: QueryDocumentSnapshot<DocumentData>,
  automationId: string,
): AutomationRun => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    automationId,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    triggeredBy: typeof data.triggeredBy === 'string' ? data.triggeredBy : '',
    triggerEventId: typeof data.triggerEventId === 'string' ? data.triggerEventId : undefined,
    triggerEventSummary: typeof data.triggerEventSummary === 'string' ? data.triggerEventSummary : undefined,
    outcome: (data.outcome as AutomationRunOutcome) ?? 'FAILED',
    startedAt: data.startedAt ?? null,
    finishedAt: data.finishedAt ?? null,
    actionEntries: Array.isArray(data.actionEntries) ? data.actionEntries : [],
    errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : undefined,
  }
}

export const mergeAutomationRuns = (...runGroups: AutomationRun[][]): AutomationRun[] => {
  const merged = new Map<string, AutomationRun>()
  runGroups.flat().forEach((run) => {
    merged.set(`${run.automationId}:${run.id}`, run)
  })
  return [...merged.values()].sort((left, right) => {
    const leftTime = left.startedAt && typeof left.startedAt === 'object' && 'toMillis' in left.startedAt
      ? left.startedAt.toMillis()
      : 0
    const rightTime = right.startedAt && typeof right.startedAt === 'object' && 'toMillis' in right.startedAt
      ? right.startedAt.toMillis()
      : 0
    return rightTime - leftTime
  })
}

export const subscribeToAutomationRuns = (
  automationId: string,
  callback: (runs: AutomationRun[]) => void,
) => {
  if (!automationId) {
    callback([])
    return () => undefined
  }
  const q = query(
    collection(firestoreDb, 'automation_runs'),
    where('automationId', '==', automationId),
    orderBy('startedAt', 'desc'),
  )
  return onSnapshot(q, (snapshot) => {
    const runs = snapshot.docs.map((entry) => normalizeRun(entry, automationId))
    callback(runs)
  })
}

export const subscribeToAutomationGraphRuns = (
  graphId: string,
  callback: (runs: AutomationRun[]) => void,
) => {
  if (!graphId) {
    callback([])
    return () => undefined
  }
  const q = query(
    collection(firestoreDb, GRAPHS_COLLECTION, graphId, 'automation_runs'),
    orderBy('startedAt', 'desc'),
  )
  const legacyRunsQuery = query(
    collection(firestoreDb, 'automation_runs'),
    where('automationId', '==', graphId),
    orderBy('startedAt', 'desc'),
  )

  let graphRuns: AutomationRun[] = []
  let legacyRuns: AutomationRun[] = []
  const emit = () => callback(mergeAutomationRuns(graphRuns, legacyRuns))

  const unsubscribeGraphRuns = onSnapshot(q, (snapshot) => {
    graphRuns = snapshot.docs.map((entry) => normalizeRun(entry, graphId))
    emit()
  })
  const unsubscribeLegacyRuns = onSnapshot(legacyRunsQuery, (snapshot) => {
    legacyRuns = snapshot.docs.map((entry) => normalizeRun(entry, graphId))
    emit()
  })

  return () => {
    unsubscribeGraphRuns()
    unsubscribeLegacyRuns()
  }
}
